import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { checklistStackApi } from '../api/endpoints.ts';
import { useLocalizedContent } from '../i18n/localized.ts';
import { useSession } from '../state/session.tsx';
import { usePlanFeature } from './plan-gate.tsx';
import { Select } from './ui.tsx';
import { inspectionLinesFrom, standardInspectionLines, type InspectionLineSeed } from '@shared/inspection-lines.ts';

/**
 * Where a walkthrough's list of checks comes from.
 *
 * `site` is the building's own cleaning checklist — what the crew is paid to
 * do there, so the fairest thing to score them against. `standard` is the
 * built-in commercial walkthrough, for a building nobody has written a
 * checklist for yet.
 */
export type InspectionLineSource = 'site' | 'standard' | 'blank' | `template:${string}`;

export interface InspectionLineSourceOption {
  value: InspectionLineSource;
  label: string;
  lines: InspectionLineSeed[];
}

export interface InspectionLineSources {
  options: InspectionLineSourceOption[];
  /** The building's checklist when it has one, otherwise the standard walkthrough. */
  preferred: InspectionLineSource;
  /** Still finding out whether the building has a checklist of its own. */
  loading: boolean;
  /** The building's checklist or the template library could not be read. */
  errored: boolean;
  /** Read, and empty: the building has no checklist yet. */
  siteEmpty: boolean;
}

/**
 * The lists a walkthrough of `propertyId` can start from, in the reader's
 * language.
 *
 * The building's checklist is read the way an ordinary visit would compile
 * it — every layer that applies to a regular visit, with no visit type — and
 * every task is listed whatever its cadence: a monthly task is still part of
 * the standard the building is held to.
 */
export function useInspectionLineSources(propertyId: string, date: string): InspectionLineSources {
  const { t } = useTranslation();
  const { can } = useSession();
  const localized = useLocalizedContent();
  // Property checklists are a scheduling feature, read with customer access.
  const readable = usePlanFeature('scheduling') && can('customers:read');

  const site = useQuery({
    queryKey: ['inspection-site-checklist', propertyId, date],
    queryFn: () => checklistStackApi.preview(propertyId, date, null, 1),
    enabled: readable && Boolean(propertyId),
  });
  const templates = useQuery({
    queryKey: ['checklist-templates'],
    queryFn: () => checklistStackApi.templates(),
    enabled: readable,
  });

  return useMemo(() => {
    const fallbackArea = t('inspections.lineSource.fallbackArea');
    const siteLines = inspectionLinesFrom(
      (site.data?.tasks ?? []).map((task) => ({
        areaName: localized.text(task.areaName, task.areaNameTranslations),
        itemName: localized.text(task.taskName, task.taskNameTranslations),
      })),
      fallbackArea,
    );
    const standard = standardInspectionLines(localized.language);

    const options: InspectionLineSourceOption[] = [];
    if (siteLines.length > 0) {
      options.push({
        value: 'site',
        label: t('inspections.lineSource.site', { count: siteLines.length }),
        lines: siteLines,
      });
    }
    for (const template of templates.data?.templates ?? []) {
      const lines = inspectionLinesFrom(
        template.definition.sections.flatMap((section) =>
          section.tasks.map((task) => ({
            areaName: localized.text(section.name, section.nameTranslations),
            itemName: localized.text(task.name, task.nameTranslations),
          })),
        ),
        fallbackArea,
      );
      if (lines.length === 0) continue;
      options.push({
        value: `template:${template.id}`,
        label: t('inspections.lineSource.template', {
          name: localized.text(template.definition.name, template.definition.nameTranslations),
          count: lines.length,
        }),
        lines,
      });
    }
    options.push(
      { value: 'standard', label: t('inspections.lineSource.standard', { count: standard.length }), lines: standard },
      { value: 'blank', label: t('inspections.lineSource.blank'), lines: [] },
    );

    return {
      options,
      preferred: siteLines.length > 0 ? 'site' : 'standard',
      loading: site.isLoading,
      errored: site.isError || templates.isError,
      siteEmpty: site.isSuccess && siteLines.length === 0,
    };
  }, [site.data, site.isLoading, site.isError, site.isSuccess, templates.data, templates.isError, localized, t]);
}

/** The option `value` names, or the preferred one when it is unset or no longer offered. */
export function chooseLineSource(
  sources: InspectionLineSources,
  value: InspectionLineSource | null,
): InspectionLineSourceOption {
  const options = sources.options;
  return (
    options.find((option) => option.value === value) ??
    options.find((option) => option.value === sources.preferred) ??
    options[0]!
  );
}

/**
 * The "what to check" picker, with the list it would produce laid out
 * underneath — scheduling a walkthrough against a list nobody has read is how
 * a building ends up scored on a break room it does not have.
 */
export function InspectionLineSourceField({
  sources,
  chosen,
  onChange,
  includeBlank = true,
}: {
  sources: InspectionLineSources;
  chosen: InspectionLineSourceOption;
  onChange: (value: InspectionLineSource) => void;
  includeBlank?: boolean;
}) {
  const { t } = useTranslation();
  const options = includeBlank ? sources.options : sources.options.filter((option) => option.value !== 'blank');

  const areas = useMemo(() => {
    const grouped = new Map<string, string[]>();
    for (const line of chosen.lines) grouped.set(line.areaName, [...(grouped.get(line.areaName) ?? []), line.itemName]);
    return [...grouped];
  }, [chosen.lines]);

  return (
    <div>
      <Select
        label={t('inspections.lineSource.label')}
        hint={
          sources.loading
            ? t('inspections.lineSource.siteLoading')
            : sources.siteEmpty
              ? t('inspections.lineSource.noSiteChecklist')
              : t('inspections.lineSource.hint')
        }
        value={chosen.value}
        onChange={(event) => onChange(event.target.value as InspectionLineSource)}
        options={options.map((option) => ({ value: option.value, label: option.label }))}
      />
      {/*
        Said rather than swallowed: a building checklist that failed to load
        would otherwise look like a building without one, and the walkthrough
        would quietly be scored against the generic list instead.
      */}
      {sources.errored ? <p className="field-hint">{t('inspections.lineSource.loadError')}</p> : null}
      {chosen.lines.length > 0 ? (
        <details className="mb-4 text-sm">
          <summary className="cursor-pointer text-[color:var(--color-muted)]">
            {t('inspections.lineSource.summary', { count: chosen.lines.length })}
          </summary>
          <ul className="mt-2 space-y-2">
            {areas.map(([area, items]) => (
              <li key={area}>
                <p className="font-semibold">{area}</p>
                <p className="text-[color:var(--color-muted)]">{items.join(' · ')}</p>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}
